import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";

/**
 * AdminPublishers — Femcare admin surface for creating, editing, and
 * disabling publisher accounts. Admin-only, JWT-protected via useAuth.
 *
 * A publisher account owns links via two paths (server-enforced):
 *   1. Auto-mapping — link.publisher_id ∈ publisher.publisher_ids
 *   2. Manual override — publisher_links.owner_publisher_id === publisher.id
 *
 * This page manages (1). Manual overrides (2) are set from
 * AdminPublisherLinks.jsx's per-row action dialog.
 */
const adminFetch = async (path, opts = {}, token) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}${path}`;
    const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    const r = await fetch(url, { credentials: "include", ...opts, headers });
    if (!r.ok) {
        const t = await r.text().catch(() => "");
        throw new Error(t || `HTTP ${r.status}`);
    }
    return r;
};

const fmtDate = (v) => {
    if (!v) return "—";
    try {
        return new Date(v).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return String(v); }
};

// Publisher last-login is rendered in India Standard Time (IST, UTC+5:30)
// so it matches the operations team's own clock regardless of the admin's
// browser timezone. Applied ONLY to the last_login_at cell — every other
// timestamp keeps the existing locale-based formatter.
const fmtIST = (v) => {
    if (!v) return "—";
    try {
        return new Date(v).toLocaleString("en-IN", {
            dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata",
        }) + " IST";
    } catch { return String(v); }
};

export default function AdminPublishers() {
    const { user, accessToken } = useAuth();
    const nav = useNavigate();
    const [rows, setRows]     = useState([]);
    const [busy, setBusy]     = useState(false);
    const [err,  setErr]      = useState(null);
    const [msg,  setMsg]      = useState(null);
    const [creating, setCreating] = useState(false);
    const [editRow,  setEditRow]  = useState(null);
    const [tempPw,   setTempPw]   = useState(null);

    useEffect(() => {
        if (!user) return;
        if (user.role !== "admin") { nav("/"); return; }
        load();
        // eslint-disable-next-line
    }, [user]);

    const load = async () => {
        setErr(null);
        try {
            const r = await adminFetch("/api/web/admin/publishers", {}, accessToken);
            const data = await r.json();
            setRows(data.rows || []);
        } catch (e) { setErr(e.message); }
    };

    const create = async (form) => {
        setBusy(true); setErr(null); setMsg(null);
        try {
            const r = await adminFetch("/api/web/admin/publishers", {
                method: "POST", body: JSON.stringify(form),
            }, accessToken);
            const data = await r.json();
            setCreating(false);
            // Server-generated one-time password → surface via the same
            // amber banner used for the reset flow.
            if (data.temporary_password) setTempPw({ id: data.publisher?.id, pw: data.temporary_password });
            else setMsg("Publisher created.");
            await load();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    };

    const patch = async (id, body) => {
        setBusy(true); setErr(null); setMsg(null);
        try {
            const r = await adminFetch(`/api/web/admin/publishers/${id}`, {
                method: "PATCH", body: JSON.stringify(body),
            }, accessToken);
            const data = await r.json();
            if (data.temporary_password) setTempPw({ id, pw: data.temporary_password });
            else setMsg("Saved.");
            await load();
            return data;
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    };

    if (!user || user.role !== "admin") return null;

    return (
        <div className="min-h-screen bg-femcare-cream/40 py-10" data-testid="admin-publishers-page">
            <div className="max-w-6xl mx-auto px-6 space-y-6">
                <header className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-femcare-plum font-semibold">Femcare Admin</div>
                        <h1 className="text-2xl font-bold text-femcare-ink mt-1">Publishers</h1>
                        <p className="text-xs text-femcare-ink/55 mt-1">
                            Manage publisher accounts. Each account can only see its own links + confirmed conversions.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/admin/publisher-links" className="text-xs text-femcare-plum hover:underline">← Publisher Links</Link>
                        <button onClick={()=>{setCreating(true); setTempPw(null);}}
                                className="rounded-xl bg-femcare-plum text-white text-sm font-semibold px-5 py-2.5"
                                data-testid="pub-create-open">Create Publisher</button>
                    </div>
                </header>

                {err && <div className="rounded-xl bg-red-50 text-red-800 px-4 py-3 text-sm" data-testid="pub-err">{err}</div>}
                {msg && <div className="rounded-xl bg-emerald-50 text-emerald-800 px-4 py-3 text-sm" data-testid="pub-msg">{msg}</div>}
                {tempPw && (
                    <div className="rounded-xl bg-amber-50 text-amber-900 px-4 py-3 text-sm" data-testid="pub-temp-pw">
                        Temporary password issued: <span className="font-mono font-bold">{tempPw.pw}</span> — copy and share
                        with the publisher now. It is not stored anywhere and will not appear again.
                    </div>
                )}

                <section className="bg-white rounded-2xl border border-femcare-border overflow-x-auto">
                    <table className="w-full text-sm" data-testid="pub-table">
                        <thead className="bg-femcare-cream/40 text-xs uppercase tracking-wider text-femcare-ink/70">
                            <tr className="text-left">
                                <th className="px-4 py-3">Publisher</th>
                                <th className="px-4 py-3">Publisher IDs</th>
                                <th className="px-4 py-3">Links</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Last Login</th>
                                <th className="px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-10 text-center text-femcare-ink/50">No publishers yet.</td></tr>
                        )}
                        {rows.map((p) => (
                            <tr key={p.id} className="border-t border-femcare-border" data-testid={`pub-row-${p.id}`}>
                                <td className="px-4 py-3">
                                    <div className="font-semibold text-femcare-plum">{p.display_name || p.email}</div>
                                    <div className="text-[10px] text-femcare-ink/55">{p.email}</div>
                                </td>
                                <td className="px-4 py-3 text-xs font-mono">
                                    {(p.publisher_ids || []).join(", ") || <span className="text-femcare-ink/40">—</span>}
                                </td>
                                <td className="px-4 py-3">{p.links_owned ?? 0}</td>
                                <td className="px-4 py-3">
                                    {p.active ? (
                                        <span className="inline-block rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 text-xs font-semibold">Active</span>
                                    ) : (
                                        <span className="inline-block rounded-full bg-red-50 text-red-800 px-2 py-0.5 text-xs font-semibold">Disabled</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-xs">{fmtIST(p.last_login_at)}</td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        <button onClick={()=>setEditRow(p)} className="text-xs font-semibold text-femcare-plum hover:underline" data-testid={`pub-edit-${p.id}`}>Edit</button>
                                        <button onClick={()=>patch(p.id, { action: "reset_password" })} className="text-xs font-semibold text-femcare-plum hover:underline" data-testid={`pub-reset-${p.id}`}>Reset Password</button>
                                        <button onClick={()=>patch(p.id, { action: "set_active", active: !p.active })} className="text-xs font-semibold text-femcare-ink/60 hover:text-red-700" data-testid={`pub-toggle-${p.id}`}>
                                            {p.active ? "Disable" : "Enable"}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </section>
            </div>

            {creating && <CreateDialog onCancel={()=>setCreating(false)} onSave={create} busy={busy} />}
            {editRow &&  <EditDialog row={editRow} onCancel={()=>setEditRow(null)}
                                     onSave={(fields)=>patch(editRow.id, { action: "update", fields }).then(()=>setEditRow(null))}
                                     busy={busy} />}
        </div>
    );
}

function CreateDialog({ onCancel, onSave, busy }) {
    const [f, setF] = useState({ email: "", display_name: "", publisher_ids: "" });
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="pub-create-modal">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
                <h2 className="text-lg font-bold text-femcare-plum">Create Publisher</h2>
                <Field label="Email" value={f.email}         onChange={(v)=>setF({...f, email:v})}         type="email" tid="pub-c-email" />
                <Field label="Display Name" value={f.display_name} onChange={(v)=>setF({...f, display_name:v})} tid="pub-c-name" />
                <Field label="Publisher IDs (comma separated)" value={f.publisher_ids} onChange={(v)=>setF({...f, publisher_ids:v})} tid="pub-c-ids" />
                <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3">
                    Femcare generates a secure random password server-side and shows it once on the next screen.
                    Admins never choose or view passwords — only reset (never set) is possible afterwards.
                </div>
                <div className="flex items-center gap-3 pt-2">
                    <button onClick={()=>onSave({
                        email: f.email.trim().toLowerCase(),
                        display_name: f.display_name.trim(),
                        publisher_ids: f.publisher_ids.split(",").map(s=>s.trim()).filter(Boolean),
                    })} disabled={busy || !f.email}
                            className="rounded-xl bg-femcare-plum text-white text-sm font-semibold px-5 py-2 disabled:opacity-40"
                            data-testid="pub-c-submit">
                        {busy ? "Saving…" : "Create"}
                    </button>
                    <button onClick={onCancel} className="text-xs font-semibold text-femcare-ink/60 hover:text-red-700" data-testid="pub-c-cancel">Cancel</button>
                </div>
            </div>
        </div>
    );
}

function EditDialog({ row, onCancel, onSave, busy }) {
    const [f, setF] = useState({
        email: row.email,
        display_name: row.display_name || "",
        publisher_ids: (row.publisher_ids || []).join(", "),
        active: !!row.active,
        // Payout settings — three fixed per-conversion amounts + currency.
        // Values live on the publisher record; the publisher's own dashboard
        // reads them via /publisher/commission to compute total earned.
        payout_monthly:  row.payout_monthly  ?? 0,
        payout_yearly:   row.payout_yearly   ?? 0,
        payout_lifetime: row.payout_lifetime ?? 0,
        commission_currency: row.commission_currency || "INR",
    });
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="pub-edit-modal">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-lg font-bold text-femcare-plum">Edit Publisher</h2>
                <Field label="Email" value={f.email}         onChange={(v)=>setF({...f, email:v})}         type="email" tid="pub-e-email" />
                <Field label="Display Name" value={f.display_name} onChange={(v)=>setF({...f, display_name:v})} tid="pub-e-name" />
                <Field label="Publisher IDs (comma separated)" value={f.publisher_ids} onChange={(v)=>setF({...f, publisher_ids:v})} tid="pub-e-ids" />
                <label className="flex items-center gap-2 text-sm text-femcare-ink/70">
                    <input type="checkbox" checked={f.active} onChange={(e)=>setF({...f, active:e.target.checked})} data-testid="pub-e-active" />
                    Active
                </label>

                <div className="pt-3 mt-2 border-t border-femcare-border">
                    <div className="text-xs uppercase tracking-widest text-femcare-plum font-semibold mb-2">Payout Settings</div>
                    <p className="text-[11px] text-femcare-ink/55 mb-3">
                        Per-confirmed-conversion commission paid to this publisher, split by plan tier. Publisher sees these on their dashboard.
                    </p>
                    <label className="block mb-3">
                        <div className="text-xs uppercase tracking-wider text-femcare-ink/60 mb-1">Commission Currency</div>
                        <select value={f.commission_currency}
                                onChange={(e)=>setF({...f, commission_currency:e.target.value})}
                                className="w-full rounded-xl border border-femcare-border px-3 py-2 text-sm"
                                data-testid="pub-e-currency">
                            <option value="INR">INR (₹)</option>
                            <option value="USD">USD ($)</option>
                        </select>
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                        <NumField label="Monthly"  value={f.payout_monthly}  onChange={(v)=>setF({...f, payout_monthly:v})}  tid="pub-e-payout-monthly" />
                        <NumField label="Yearly"   value={f.payout_yearly}   onChange={(v)=>setF({...f, payout_yearly:v})}   tid="pub-e-payout-yearly" />
                        <NumField label="Lifetime" value={f.payout_lifetime} onChange={(v)=>setF({...f, payout_lifetime:v})} tid="pub-e-payout-lifetime" />
                    </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                    <button onClick={()=>onSave({
                        email: f.email.trim().toLowerCase(),
                        display_name: f.display_name.trim(),
                        publisher_ids: f.publisher_ids.split(",").map(s=>s.trim()).filter(Boolean),
                        active: f.active,
                        payout_monthly:  Number(f.payout_monthly)  || 0,
                        payout_yearly:   Number(f.payout_yearly)   || 0,
                        payout_lifetime: Number(f.payout_lifetime) || 0,
                        commission_currency: f.commission_currency,
                    })} disabled={busy}
                            className="rounded-xl bg-femcare-plum text-white text-sm font-semibold px-5 py-2 disabled:opacity-40"
                            data-testid="pub-e-submit">
                        {busy ? "Saving…" : "Save"}
                    </button>
                    <button onClick={onCancel} className="text-xs font-semibold text-femcare-ink/60 hover:text-red-700" data-testid="pub-e-cancel">Cancel</button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", tid }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-wider text-femcare-ink/60 mb-1">{label}</span>
            <input type={type} value={value} onChange={(e)=>onChange(e.target.value)}
                   className="w-full rounded-xl border border-femcare-border px-3 py-2 text-sm focus:outline-none focus:border-femcare-plum"
                   data-testid={tid} />
        </label>
    );
}


function NumField({ label, value, onChange, tid }) {
    return (
        <label className="block">
            <div className="text-xs uppercase tracking-wider text-femcare-ink/60 mb-1">{label}</div>
            <input type="number" min="0" step="1" value={value}
                   onChange={(e)=>onChange(e.target.value)}
                   className="w-full rounded-xl border border-femcare-border px-3 py-2 text-sm focus:outline-none focus:border-femcare-plum"
                   data-testid={tid} />
        </label>
    );
}

